import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export type ModalDialogIcon = 'success' | 'error' | 'warning' | 'info' | 'question';
export type DialogSize = 'small' | 'medium' | 'large' | 'xlarge';
export interface ModalDialogData {
  icon: ModalDialogIcon;
  title: string;
  message: string;
  htmlMessage?: boolean;
  confirmButtonText?: string;
  cancelButtonText?: string;
  showCancelButton?: boolean;
  size?: DialogSize;
}
export interface ModalResult {
  isConfirmed: boolean;
  isCancelled: boolean;
  isClosed: boolean;
}
@Component({
  selector: 'app-modal-dialog',
  standalone: true,
  imports: [MatDialogModule, MatIconModule, MatButtonModule],
  template: `
    <div class="modal-dialog__header">
      <h2 mat-dialog-title class="modal-dialog__title">
        @if (data.icon) {
          <div class="modal-dialog__icon-container">
            <mat-icon [class]="'modal-dialog__icon--' + data.icon">{{ iconMap[data.icon] }}</mat-icon>
          </div>
        }
        {{ data.title }}
      </h2>
      <button mat-icon-button class="modal-dialog__close" color="warn" (click)="onClose('closed')" aria-label="Cerrar diálogo">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <mat-dialog-content class="modal-dialog__content">
      @if (data.htmlMessage) {
        <p class="modal-dialog__message" [innerHTML]="safeHtmlMessage"></p>
      } @else {
        <p class="modal-dialog__message">{{ data.message }}</p>
      }
    </mat-dialog-content>

    <mat-dialog-actions align="end" class="modal-dialog__actions">
      @if (data.showCancelButton) {
        <button mat-button class="modal-dialog__button modal-dialog__button--cancel" (click)="onClose('cancel')">
          {{ data.cancelButtonText || 'Cancelar' }}
        </button>
      }
      <button mat-button class="modal-dialog__button modal-dialog__button--confirm" (click)="onClose('confirm')">
        {{ data.confirmButtonText || 'Aceptar' }}
      </button>
    </mat-dialog-actions>
  `,
  styleUrls: ['./modal-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModalDialogComponent implements OnInit {
  readonly data: ModalDialogData = inject(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<ModalDialogComponent, 'confirm' | 'cancel'>);
  private readonly sanitizer = inject(DomSanitizer);
  safeHtmlMessage: SafeHtml | null = null;

  private readonly iconMap: Record<ModalDialogIcon, string> = {
    success: 'check_circle',
    error: 'gpp_bad',
    warning: 'warning',
    info: 'info',
    question: 'help'
  };

  ngOnInit(): void {
    this.setSize();
    this.sanitizeMessage();
  }

  private setSize(): void {
    if (!this.data.size) return;

    const sizeMap: Record<DialogSize, string> = {
      small: '380px',
      medium: '500px',
      large: '700px',
      xlarge: '900px'
    };
    this.dialogRef.updateSize(sizeMap[this.data.size]);
  }

  private sanitizeMessage(): void {
    if (this.data.htmlMessage) {
      this.safeHtmlMessage = this.sanitizer.bypassSecurityTrustHtml(this.data.message);
    }
  }

  onClose(result: 'confirm' | 'cancel' | 'closed'): void {
    this.dialogRef.close(result);
  }
}
